import { Fragment, createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

const sectionLabels: Record<string, string> = {
  users: 'Utilizadores',
  applications: 'Aplicações',
  analytics: 'Análise',
  requests: 'Pedidos de acesso',
  audit: 'Auditoria',
  security: 'A minha segurança',
  settings: 'Definições',
};

export interface Crumb {
  label: string;
  to: string;
}

const EntityContext = createContext<{ trail: Crumb[]; setTrail: (value: Crumb[]) => void }>({ trail: [], setTrail: () => {} });

export function BreadcrumbEntityProvider({ children }: { children: ReactNode }) {
  const [trail, setTrail] = useState<Crumb[]>([]);
  const value = useMemo(() => ({ trail, setTrail }), [trail]);
  return <EntityContext.Provider value={value}>{children}</EntityContext.Provider>;
}

/**
 * Páginas de detalhe publicam a trilha de crumbs depois da secção.
 * Aceita o nome simples da entidade ou vários níveis para páginas aninhadas.
 */
export function useBreadcrumbTrail(...trail: Array<Crumb | string | undefined>) {
  const { setTrail } = useContext(EntityContext);
  const resolved = trail
    .filter((crumb): crumb is Crumb | string => Boolean(crumb))
    .map((crumb) => (typeof crumb === 'string' ? { label: crumb, to: '' } : crumb));
  const key = resolved.map((crumb) => `${crumb.label}|${crumb.to}`).join('>');
  useEffect(() => {
    setTrail(resolved);
    return () => setTrail([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setTrail]);
}

/** Atalho para o caso comum de um único nível (nome da entidade). */
export function useBreadcrumbEntity(name?: string) {
  useBreadcrumbTrail(name);
}

export function HeaderBreadcrumb() {
  const location = useLocation();
  const { trail } = useContext(EntityContext);
  const segments = location.pathname.split('/').filter(Boolean);
  const section = segments[0] ? sectionLabels[segments[0]] : undefined;
  const detailTrail = segments.length > 1
    ? (trail.length > 0 ? trail : [{ label: 'Detalhe', to: location.pathname }])
    : [];

  const crumbs: Crumb[] = [
    { label: 'Consola administrativa', to: '/' },
    ...(section ? [{ label: section, to: `/${segments[0]}` }] : []),
    ...detailTrail,
  ];

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <Fragment key={`${crumb.label}-${index}`}>
              {index > 0 && <BreadcrumbSeparator className="hidden sm:block" />}
              <BreadcrumbItem className={isLast ? undefined : 'hidden sm:inline-flex'}>
                {isLast || !crumb.to ? (
                  <BreadcrumbPage className="max-w-56 truncate">{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.to}>{crumb.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
